import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Sparkles, Loader2, RefreshCw, ArrowRight, ArrowLeft, ClipboardList, Trash2, Save,
  FileDown, AlertTriangle, CheckCircle2, Package, Truck, Trophy, Percent, Calendar,
} from 'lucide-react';

const URGENCY_STYLES = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 border-red-200',
  high:     'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300 border-orange-200',
  medium:   'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border-amber-200',
  low:      'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200',
};
const GRADE_TONE = { 'A+': 'text-emerald-600', A: 'text-emerald-600', B: 'text-sky-600', C: 'text-amber-600', D: 'text-orange-600', F: 'text-red-600' };

const CURRENCY = (n) => (Number(n) || 0).toLocaleString(undefined, { style: 'currency', currency: 'GHS', maximumFractionDigits: 2 });

function UrgencyBadge({ urgency }) {
  return <Badge variant="outline" className={`text-[10px] uppercase ${URGENCY_STYLES[urgency] || URGENCY_STYLES.low}`}>{urgency}</Badge>;
}

function GradeChip({ grade }) {
  if (!grade) return <span className="text-xs text-muted-foreground">—</span>;
  return <span className={`text-xs font-semibold ${GRADE_TONE[grade] || 'text-muted-foreground'}`} title={`Scorecard grade ${grade}`}>{grade}</span>;
}

export default function PurchasePlannerPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState({
    run_rate_days: 60,
    default_lead_time_days: 7,
    safety_days: 3,
    category: '',
    min_urgency: 'low',
    monthly_budget: '',
  });
  const [plan, setPlan] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [rows, setRows] = useState([]);          // editable copy of items
  const [suppliers, setSuppliers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [filter, setFilter] = useState({ urgency: 'all', keep: 'kept' });

  const loadTemplates = useCallback(async () => {
    try {
      const res = await api.get('/purchase-planner/templates');
      setTemplates(res.data?.templates || []);
    } catch {/* no-op */}
  }, []);

  useEffect(() => {
    loadTemplates();
    api.get('/suppliers?limit=200').then(res => {
      setSuppliers(res.data?.suppliers || res.data || []);
    }).catch(() => {});
  }, [loadTemplates]);

  const runAnalyze = async (cfg = config) => {
    setAnalyzing(true);
    setPlan(null);
    try {
      const body = { ...cfg, monthly_budget: cfg.monthly_budget ? Number(cfg.monthly_budget) : null };
      const res = await api.post('/purchase-planner/analyze', body);
      setPlan(res.data);
      setRows((res.data.items || []).map(i => ({
        ...i,
        override_qty: i.suggested_qty,
        override_supplier_id: i.suggested_supplier?.supplier_id || '',
        override_unit_cost: i.suggested_supplier?.last_unit_cost || 0,
        keep: i.suggested_qty > 0,
      })));
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const applyTemplate = async (tid) => {
    setAnalyzing(true);
    try {
      const res = await api.post(`/purchase-planner/templates/${tid}/apply`);
      setPlan(res.data);
      setConfig(prev => ({ ...prev, ...(res.data?.config || {}), monthly_budget: res.data?.config?.monthly_budget || '' }));
      setRows((res.data.items || []).map(i => ({
        ...i,
        override_qty: i.suggested_qty,
        override_supplier_id: i.suggested_supplier?.supplier_id || '',
        override_unit_cost: i.suggested_supplier?.last_unit_cost || 0,
        keep: i.suggested_qty > 0,
      })));
      setStep(2);
      toast.success('Template applied');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Template apply failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) return toast.error('Template name required');
    setSavingTemplate(true);
    try {
      await api.post('/purchase-planner/templates', {
        name: templateName.trim(),
        ...config,
        monthly_budget: config.monthly_budget ? Number(config.monthly_budget) : null,
      });
      toast.success('Template saved');
      setTemplateName('');
      loadTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally { setSavingTemplate(false); }
  };

  const deleteTemplate = async (tid) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/purchase-planner/templates/${tid}`);
      toast.success('Template deleted');
      loadTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  const updateRow = (product_id, patch) => {
    setRows(prev => prev.map(r => r.product_id === product_id ? { ...r, ...patch } : r));
  };

  const bulk = (action) => {
    setRows(prev => prev.map(r => {
      if (action === 'keep-critical') return { ...r, keep: r.urgency === 'critical' ? true : r.keep };
      if (action === 'skip-low')      return { ...r, keep: r.urgency === 'low' ? false : r.keep };
      if (action === 'keep-all')      return { ...r, keep: true };
      if (action === 'skip-all')      return { ...r, keep: false };
      return r;
    }));
  };

  const filteredRows = useMemo(() => rows.filter(r => {
    if (filter.urgency !== 'all' && r.urgency !== filter.urgency) return false;
    if (filter.keep === 'kept' && !r.keep) return false;
    if (filter.keep === 'skipped' && r.keep) return false;
    return true;
  }), [rows, filter]);

  // Live totals & supplier consolidation
  const commitItems = useMemo(() => rows
    .filter(r => r.keep && Number(r.override_qty) > 0 && r.override_supplier_id)
    .map(r => {
      const supplier = [r.suggested_supplier, ...(r.alt_suppliers || [])].find(s => s?.supplier_id === r.override_supplier_id)
                     || suppliers.find(s => s.supplier_id === r.override_supplier_id);
      return {
        product_id: r.product_id,
        product_name: r.product_name,
        quantity: Number(r.override_qty),
        unit_cost: Number(r.override_unit_cost) || 0,
        supplier_id: r.override_supplier_id,
        supplier_name: supplier?.supplier_name || supplier?.name || '',
        branch_breakdown: r.branch_breakdown || {},
      };
    }), [rows, suppliers]);

  const supplierGroups = useMemo(() => {
    const map = new Map();
    for (const it of commitItems) {
      const key = it.supplier_id;
      if (!map.has(key)) map.set(key, { supplier_id: it.supplier_id, supplier_name: it.supplier_name, items: [], subtotal: 0 });
      const g = map.get(key);
      g.items.push(it);
      g.subtotal += it.unit_cost * it.quantity;
    }
    return Array.from(map.values());
  }, [commitItems]);

  const totalValue = supplierGroups.reduce((s, g) => s + g.subtotal, 0);
  const overBudget = config.monthly_budget && totalValue > Number(config.monthly_budget);

  const commitPlan = async () => {
    if (!commitItems.length) return toast.error('Nothing to commit — approve at least one item');
    setCommitting(true);
    try {
      const res = await api.post('/purchase-planner/commit', {
        items: commitItems,
        summary: {
          run_rate_days: config.run_rate_days,
          safety_days: config.safety_days,
          default_lead_time_days: config.default_lead_time_days,
          items_flagged: rows.length,
        },
        notes: `Planner batch · ${supplierGroups.length} suppliers · ${commitItems.length} SKUs`,
      });
      toast.success(`Created ${res.data.supplier_count} draft PO${res.data.supplier_count === 1 ? '' : 's'} · total ${CURRENCY(res.data.total_value)}`);
      navigate('/purchases');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Commit failed');
    } finally { setCommitting(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.post('/purchase-planner/analyze/pdf', { plan }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `purchase-plan-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('PDF export failed');
    }
  };

  const supplierOptions = (row) => {
    const seen = new Set();
    const opts = [];
    for (const s of [row.suggested_supplier, ...(row.alt_suppliers || [])]) {
      if (s?.supplier_id && !seen.has(s.supplier_id)) {
        seen.add(s.supplier_id);
        opts.push(s);
      }
    }
    for (const s of suppliers) {
      const sid = s.supplier_id;
      if (sid && !seen.has(sid)) {
        seen.add(sid);
        opts.push({ supplier_id: sid, supplier_name: s.name, grade: null, last_unit_cost: 0 });
      }
    }
    return opts;
  };

  return (
    <div className="space-y-6" data-testid="purchase-planner-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Sparkles className="h-6 w-6 text-primary" /> Purchase Planner</h1>
          <p className="text-sm text-muted-foreground mt-1">Real needs → run rate → smart supplier pick → approve → draft POs, one per supplier.</p>
        </div>
        <Tabs value={String(step)} className="hidden md:block">
          <TabsList>
            <TabsTrigger value="1" onClick={() => setStep(1)}>1 · Configure</TabsTrigger>
            <TabsTrigger value="2" onClick={() => plan && setStep(2)} disabled={!plan}>2 · Review</TabsTrigger>
            <TabsTrigger value="3" onClick={() => commitItems.length && setStep(3)} disabled={!commitItems.length}>3 · Confirm</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {step === 1 && (
        <>
          <Card data-testid="planner-config-card">
            <CardHeader>
              <CardTitle className="text-base">Analysis Settings</CardTitle>
              <CardDescription>Tune the demand-signal parameters. Supplier lead times are pulled per-supplier and fall back to this default when unset.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Run-rate window (days)</Label>
                <Input type="number" min={7} max={365} value={config.run_rate_days} onChange={e => setConfig(p => ({ ...p, run_rate_days: Number(e.target.value) }))} data-testid="planner-run-rate-input" />
              </div>
              <div>
                <Label>Default lead time (days)</Label>
                <Input type="number" min={1} max={90} value={config.default_lead_time_days} onChange={e => setConfig(p => ({ ...p, default_lead_time_days: Number(e.target.value) }))} data-testid="planner-lead-time-input" />
              </div>
              <div>
                <Label>Safety days (buffer)</Label>
                <Input type="number" min={0} max={30} value={config.safety_days} onChange={e => setConfig(p => ({ ...p, safety_days: Number(e.target.value) }))} data-testid="planner-safety-days-input" />
              </div>
              <div>
                <Label>Category filter</Label>
                <Input placeholder="e.g. Antibiotics (leave blank for all)" value={config.category} onChange={e => setConfig(p => ({ ...p, category: e.target.value }))} data-testid="planner-category-input" />
              </div>
              <div>
                <Label>Minimum urgency</Label>
                <Select value={config.min_urgency} onValueChange={v => setConfig(p => ({ ...p, min_urgency: v }))}>
                  <SelectTrigger data-testid="planner-min-urgency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low (include all needs)</SelectItem>
                    <SelectItem value="medium">Medium or higher</SelectItem>
                    <SelectItem value="high">High or critical</SelectItem>
                    <SelectItem value="critical">Critical only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Monthly procurement budget (optional)</Label>
                <Input type="number" min={0} value={config.monthly_budget} onChange={e => setConfig(p => ({ ...p, monthly_budget: e.target.value }))} placeholder="e.g. 50000" data-testid="planner-budget-input" />
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => runAnalyze()} disabled={analyzing} className="gap-1.5" data-testid="planner-analyze-btn">
              {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Run analysis
            </Button>
            <div className="flex items-center gap-2 ml-auto">
              <Input placeholder="Save current config as template…" value={templateName} onChange={e => setTemplateName(e.target.value)} className="w-64" data-testid="planner-template-name-input" />
              <Button variant="outline" size="sm" onClick={saveTemplate} disabled={savingTemplate || !templateName.trim()} className="gap-1.5" data-testid="planner-template-save-btn">
                {savingTemplate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save template
              </Button>
            </div>
          </div>

          {templates.length > 0 && (
            <Card data-testid="planner-templates-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" /> Saved templates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {templates.map(t => (
                  <div key={t.template_id} className="flex items-center justify-between gap-3 p-2 rounded border hover:bg-muted/30 transition-colors">
                    <div>
                      <p className="text-sm font-medium">{t.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Run rate {t.config?.run_rate_days}d · Lead {t.config?.default_lead_time_days}d · Safety {t.config?.safety_days}d · {t.config?.min_urgency}+ urgency
                        {t.config?.monthly_budget ? ` · budget ${CURRENCY(t.config.monthly_budget)}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="outline" onClick={() => applyTemplate(t.template_id)} disabled={analyzing} className="gap-1" data-testid={`planner-template-apply-${t.template_id}`}>
                        <RefreshCw className="h-3 w-3" /> Run
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteTemplate(t.template_id)} className="text-red-600" data-testid={`planner-template-delete-${t.template_id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {step === 2 && plan && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <StatCard icon={Package} label="Items flagged" value={plan.summary.items_flagged} tone="text-primary" />
            <StatCard icon={AlertTriangle} label="Critical" value={plan.summary.critical} tone="text-red-600" />
            <StatCard icon={AlertTriangle} label="High" value={plan.summary.high} tone="text-orange-600" />
            <StatCard icon={Truck} label="Suppliers" value={plan.summary.supplier_count} tone="text-sky-600" />
            <StatCard icon={Percent} label="Est. total" value={CURRENCY(plan.summary.estimated_total_cost)} tone="text-emerald-600" small />
          </div>

          {plan.summary.budget_utilisation_pct != null && (
            <div className={`p-3 rounded-lg border flex items-center gap-2 text-sm ${plan.summary.budget_utilisation_pct > 100 ? 'bg-red-50 dark:bg-red-950/30 border-red-200 text-red-800' : 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 text-emerald-800'}`} data-testid="planner-budget-line">
              Budget utilisation: <span className="font-semibold">{plan.summary.budget_utilisation_pct}%</span>
              {plan.summary.budget_utilisation_pct > 100 && <span className="ml-2">— trimming quantities recommended</span>}
            </div>
          )}

          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-3">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-primary" />
                <CardTitle className="text-base">Review &amp; approve — {filteredRows.length} of {rows.length} shown</CardTitle>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={filter.urgency} onValueChange={v => setFilter(p => ({ ...p, urgency: v }))}>
                  <SelectTrigger className="w-36" data-testid="planner-filter-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All urgencies</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filter.keep} onValueChange={v => setFilter(p => ({ ...p, keep: v }))}>
                  <SelectTrigger className="w-32" data-testid="planner-filter-keep"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kept">Kept</SelectItem>
                    <SelectItem value="skipped">Skipped</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={() => bulk('keep-critical')} data-testid="planner-bulk-keep-critical">Keep all critical</Button>
                <Button size="sm" variant="outline" onClick={() => bulk('skip-low')} data-testid="planner-bulk-skip-low">Skip all low</Button>
                <Button size="sm" variant="ghost" onClick={() => bulk('keep-all')}>Keep all</Button>
                <Button size="sm" variant="ghost" onClick={() => bulk('skip-all')}>Skip all</Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-x-auto pt-0">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left">Keep</th>
                    <th className="p-2 text-left">Product</th>
                    <th className="p-2 text-right">Stock</th>
                    <th className="p-2 text-right">On-order</th>
                    <th className="p-2 text-right">Run/day</th>
                    <th className="p-2 text-right">Days cover</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2 text-left">Supplier</th>
                    <th className="p-2 text-right">Unit ₵</th>
                    <th className="p-2 text-right">Est cost</th>
                    <th className="p-2 text-left">Urgency</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.slice(0, 300).map(r => {
                    const options = supplierOptions(r);
                    return (
                      <tr key={r.product_id} className="border-t hover:bg-muted/20" data-testid={`planner-row-${r.product_id}`}>
                        <td className="p-2"><Checkbox checked={r.keep} onCheckedChange={v => updateRow(r.product_id, { keep: !!v })} data-testid={`planner-row-keep-${r.product_id}`} /></td>
                        <td className="p-2">
                          <div className="font-medium">{r.product_name}</div>
                          <div className="text-[11px] text-muted-foreground">{r.category} · {r.reason}</div>
                        </td>
                        <td className="p-2 text-right">{r.current_stock}</td>
                        <td className="p-2 text-right">{r.on_order}</td>
                        <td className="p-2 text-right">{r.run_rate_per_day?.toFixed(1)}</td>
                        <td className="p-2 text-right">{r.days_of_cover > 500 ? '∞' : r.days_of_cover}</td>
                        <td className="p-2 text-right">
                          <Input type="number" min={0} value={r.override_qty} onChange={e => updateRow(r.product_id, { override_qty: Number(e.target.value) })} className="h-8 w-20 text-right" data-testid={`planner-row-qty-${r.product_id}`} />
                        </td>
                        <td className="p-2">
                          <Select value={r.override_supplier_id || ''} onValueChange={v => {
                            const supplier = options.find(s => s.supplier_id === v);
                            updateRow(r.product_id, { override_supplier_id: v, override_unit_cost: supplier?.last_unit_cost || r.override_unit_cost });
                          }}>
                            <SelectTrigger className="h-8 w-48" data-testid={`planner-row-supplier-${r.product_id}`}><SelectValue placeholder="Pick supplier" /></SelectTrigger>
                            <SelectContent>
                              {options.map(s => (
                                <SelectItem key={s.supplier_id} value={s.supplier_id}>
                                  <span className="flex items-center gap-2">
                                    {s.supplier_name}
                                    {s.grade && <GradeChip grade={s.grade} />}
                                    {s.supplier_id === r.suggested_supplier?.supplier_id && <span className="text-[10px] text-emerald-600 uppercase">recommended</span>}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-2 text-right">
                          <Input type="number" min={0} step="0.01" value={r.override_unit_cost} onChange={e => updateRow(r.product_id, { override_unit_cost: Number(e.target.value) })} className="h-8 w-24 text-right" data-testid={`planner-row-cost-${r.product_id}`} />
                        </td>
                        <td className="p-2 text-right font-medium">{CURRENCY((Number(r.override_qty) || 0) * (Number(r.override_unit_cost) || 0))}</td>
                        <td className="p-2"><UrgencyBadge urgency={r.urgency} /></td>
                      </tr>
                    );
                  })}
                  {filteredRows.length === 0 && (
                    <tr><td colSpan={11} className="text-center p-6 text-sm text-muted-foreground">No items match the current filter.</td></tr>
                  )}
                </tbody>
                {rows.length > 0 && (
                  <tfoot className="bg-muted/40 text-sm">
                    <tr>
                      <td colSpan={9} className="p-2 text-right font-semibold">Running total (kept items)</td>
                      <td className="p-2 text-right font-bold" data-testid="planner-running-total">{CURRENCY(totalValue)}</td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </CardContent>
          </Card>

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setStep(1)} className="gap-1.5" data-testid="planner-back-1"><ArrowLeft className="h-4 w-4" /> Back to settings</Button>
            <Button variant="outline" onClick={downloadPdf} className="gap-1.5" data-testid="planner-pdf-btn"><FileDown className="h-4 w-4" /> Export PDF</Button>
            <div className="ml-auto flex items-center gap-2">
              {overBudget && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Over budget by {CURRENCY(totalValue - Number(config.monthly_budget))}</Badge>}
              <Button onClick={() => setStep(3)} disabled={!commitItems.length} className="gap-1.5" data-testid="planner-goto-confirm">
                Review PO drafts <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Draft purchase orders</CardTitle>
                <CardDescription>One PO per supplier — {supplierGroups.length} supplier{supplierGroups.length === 1 ? '' : 's'} · {commitItems.length} SKUs · total {CURRENCY(totalValue)}</CardDescription>
              </div>
              {overBudget && (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 gap-1"><AlertTriangle className="h-3 w-3" /> Over monthly budget</Badge>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {supplierGroups.map(g => (
                <div key={g.supplier_id} className="border rounded-lg overflow-hidden" data-testid={`planner-confirm-group-${g.supplier_id}`}>
                  <div className="flex items-center justify-between p-3 bg-muted/40">
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-primary" />
                      <span className="font-semibold">{g.supplier_name}</span>
                      <span className="text-xs text-muted-foreground">{g.items.length} item{g.items.length === 1 ? '' : 's'}</span>
                    </div>
                    <span className="font-semibold">{CURRENCY(g.subtotal)}</span>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-muted-foreground">
                      <tr><th className="p-2 text-left">Product</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Unit ₵</th><th className="p-2 text-right">Line total</th></tr>
                    </thead>
                    <tbody>
                      {g.items.map(it => (
                        <tr key={it.product_id} className="border-t">
                          <td className="p-2">{it.product_name}</td>
                          <td className="p-2 text-right">{it.quantity}</td>
                          <td className="p-2 text-right">{CURRENCY(it.unit_cost)}</td>
                          <td className="p-2 text-right font-medium">{CURRENCY(it.unit_cost * it.quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </CardContent>
          </Card>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setStep(2)} className="gap-1.5" data-testid="planner-back-2"><ArrowLeft className="h-4 w-4" /> Back to review</Button>
            <Button onClick={commitPlan} disabled={committing || !commitItems.length} className="ml-auto gap-1.5" data-testid="planner-commit-btn">
              {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Create {supplierGroups.length} draft PO{supplierGroups.length === 1 ? '' : 's'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, small }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <Icon className={`h-3.5 w-3.5 ${tone}`} /> {label}
        </div>
        <div className={`mt-1 font-bold ${tone} ${small ? 'text-xl' : 'text-2xl'}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
